import React, { useRef } from 'react'
import { AppearanceSectionProps } from './types'
import {
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  ColorInput,
  RangeInput,
  RangeValue,
  FlexRow,
  Checkbox,
} from '../styled'

const AppearanceSection: React.FC<AppearanceSectionProps> = ({
  selectedElement,
  localProperties,
  isCollapsed,
  onToggleSection,
  updateProperty,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const getColorLabel = () => {
    switch (selectedElement.type) {
      case 'text':
        return 'Text Colour'
      case 'poi':
      case 'fixture-point':
        return 'Point Colour'
      default:
        return 'Stroke Colour'
    }
  }

  const showStrokeWidth = () => {
    return (
      selectedElement.type !== 'text' &&
      selectedElement.type !== 'stairs' &&
      selectedElement.type !== 'poi' &&
      selectedElement.type !== 'fixture-point'
    )
  }

  const showFillColor = () => {
    return (
      selectedElement.type === 'rectangle' ||
      selectedElement.type === 'circle' ||
      selectedElement.type === 'room' ||
      selectedElement.type === 'polygon' ||
      selectedElement.type === 'fixture-rect' ||
      selectedElement.type === 'fixture-circle' ||
      selectedElement.type === 'fixture-polygon'
    )
  }

  const showBackgroundImage = () => {
    return (
      selectedElement.type === 'rectangle' ||
      selectedElement.type === 'circle' ||
      selectedElement.type === 'room' ||
      selectedElement.type === 'polygon' ||
      selectedElement.type === 'fixture-rect' ||
      selectedElement.type === 'fixture-circle' ||
      selectedElement.type === 'fixture-polygon'
    )
  }

  return (
    <PropertySection>
      <SectionHeader isCollapsed={isCollapsed} onClick={onToggleSection}>
        <SectionTitle>Appearance</SectionTitle>
        <CollapseIcon isCollapsed={isCollapsed}>▼</CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={isCollapsed}>
        <PropertyGroup>
          <Label>{getColorLabel()}</Label>
          <FlexRow>
            <ColorInput
              value={localProperties.color || '#333333'}
              onChange={e => updateProperty('color', e.target.value)}
            />
            <Input
              value={localProperties.color || '#333333'}
              onChange={e => updateProperty('color', e.target.value)}
              placeholder="#333333"
            />
          </FlexRow>
        </PropertyGroup>

        {showStrokeWidth() && (
          <PropertyGroup>
            <Label>Stroke Width</Label>
            <FlexRow>
              <RangeInput
                min="0"
                max="10"
                value={localProperties.strokeWidth ?? 2}
                onChange={e =>
                  updateProperty('strokeWidth', Number(e.target.value))
                }
              />
              <RangeValue>{localProperties.strokeWidth ?? 2}px</RangeValue>
            </FlexRow>
          </PropertyGroup>
        )}

        {showFillColor() && (
          <PropertyGroup>
            <Label>Fill Colour</Label>
            <FlexRow>
              <ColorInput
                value={localProperties.fillColor || '#transparent'}
                onChange={e => updateProperty('fillColor', e.target.value)}
              />
              <Input
                value={localProperties.fillColor || 'transparent'}
                onChange={e => updateProperty('fillColor', e.target.value)}
                placeholder="transparent"
              />
            </FlexRow>
          </PropertyGroup>
        )}

        <PropertyGroup>
          <Label>Opacity</Label>
          <FlexRow>
            <RangeInput
              min="0"
              max="1"
              step="0.01"
              value={
                localProperties.opacity !== undefined
                  ? localProperties.opacity
                  : 1
              }
              onChange={e => updateProperty('opacity', Number(e.target.value))}
            />
            <RangeValue>
              {Math.round(
                (localProperties.opacity !== undefined
                  ? localProperties.opacity
                  : 1) * 100,
              )}
              %
            </RangeValue>
          </FlexRow>
        </PropertyGroup>

        {showBackgroundImage() && (
          <>
            <PropertyGroup>
              <Label>Background Image</Label>
              <Input
                value={localProperties.backgroundImage || ''}
                onChange={e => updateProperty('backgroundImage', e.target.value)}
                placeholder="https://example.com/image.jpg or upload below"
              />
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '6px 12px',
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  📁 Upload Image
                </button>
                {localProperties.backgroundImage && (
                  <button
                    onClick={() => updateProperty('backgroundImage', '')}
                    style={{
                      padding: '6px 12px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      updateProperty('backgroundImage', reader.result as string)
                    }
                    reader.readAsDataURL(file)
                  }
                }}
              />
            </PropertyGroup>
            {localProperties.backgroundImage && (
              <>
                <PropertyGroup>
                  <Label>Image Preview</Label>
                  <div style={{
                    width: '100%',
                    height: '120px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    overflow: 'hidden',
                    backgroundImage: `url(${localProperties.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }} />
                </PropertyGroup>
                <PropertyGroup>
                  <Label>Background Image Fit</Label>
                  <select
                    value={localProperties.backgroundImageFit || 'cover'}
                    onChange={e => updateProperty('backgroundImageFit', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      fontSize: '12px'
                    }}
                  >
                    <option value="cover">Cover (fills entire shape)</option>
                    <option value="contain">Contain (fits inside shape)</option>
                    <option value="stretch">Stretch (distorts to fit)</option>
                    <option value="repeat">Repeat (tiles the image)</option>
                  </select>
                </PropertyGroup>
                <PropertyGroup>
                  <Label>Background Image Size</Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="number"
                        value={localProperties.backgroundImageWidth || ''}
                        onChange={e => updateProperty('backgroundImageWidth', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Width (px)"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="number"
                        value={localProperties.backgroundImageHeight || ''}
                        onChange={e => updateProperty('backgroundImageHeight', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Height (px)"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    <Label style={{ fontSize: '11px', color: '#666' }}>
                      Leave empty to use original size
                    </Label>
                  </div>
                </PropertyGroup>
                <PropertyGroup>
                  <Label>Background Image Position</Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="number"
                        value={localProperties.backgroundImageX || 0}
                        onChange={e => updateProperty('backgroundImageX', Number(e.target.value))}
                        placeholder="X offset"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="number"
                        value={localProperties.backgroundImageY || 0}
                        onChange={e => updateProperty('backgroundImageY', Number(e.target.value))}
                        placeholder="Y offset"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                </PropertyGroup>
                <PropertyGroup>
                  <Label>Background Image Opacity</Label>
                  <FlexRow>
                    <RangeInput
                      min="0"
                      max="1"
                      step="0.01"
                      value={localProperties.backgroundImageOpacity ?? 1}
                      onChange={e => updateProperty('backgroundImageOpacity', Number(e.target.value))}
                    />
                    <RangeValue>
                      {Math.round((localProperties.backgroundImageOpacity ?? 1) * 100)}%
                    </RangeValue>
                  </FlexRow>
                </PropertyGroup>
              </>
            )}
          </>
        )}

        {(selectedElement.type === 'room' || selectedElement.type === 'polygon') && (
          <>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={localProperties.hideMeasurements !== true}
                  onChange={e => updateProperty('hideMeasurements', !e.target.checked)}
                />
                Show Measurements in Viewer
              </Label>
            </PropertyGroup>
            <PropertyGroup>
              <Label>
                <Checkbox
                  checked={localProperties.hideLabel !== true}
                  onChange={e => updateProperty('hideLabel', !e.target.checked)}
                />
                Show Label in Viewer
              </Label>
            </PropertyGroup>
          </>
        )}
      </SectionContent>
    </PropertySection>
  )
}

export { AppearanceSection }
